import { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";
import { GeocodedAddress } from "@/lib/location/types";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/error-utils";
import { cn } from "@/lib/utils";

interface AddressAutocompleteProps {
  value?: string;
  onSelect: (address: GeocodedAddress) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number];
  context?: Array<{ id: string; text: string; short_code?: string }>;
  address?: string;
  text?: string;
}

export function AddressAutocomplete({
  value = "",
  onSelect,
  placeholder = "Search for an address...",
  label,
  className,
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("geocode-address", {
        body: { address: q },
      });

      if (!error && data?.features) {
        setSuggestions(data.features.slice(0, 5));
        setIsOpen(true);
      }
    } catch (err) {
      // Autocomplete fires on every keystroke — surfacing a toast would be
      // noise. Capture for support, but stay silent in the UI.
      logError(err, "address-autocomplete");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (feature: MapboxFeature) => {
    const context = feature.context || [];
    const getContext = (type: string) =>
      context.find((c) => c.id.startsWith(type))?.text || "";
    const getShortCode = (type: string) =>
      context.find((c) => c.id.startsWith(type))?.short_code?.replace("US-", "") || "";

    const geocoded: GeocodedAddress = {
      street_address: feature.address
        ? `${feature.address} ${feature.text || ""}`
        : feature.place_name?.split(",")[0] || "",
      city: getContext("place"),
      state: getShortCode("region") || getContext("region"),
      zip_code: getContext("postcode"),
      country: getContext("country") || "US",
      latitude: feature.center[1],
      longitude: feature.center[0],
      formatted_address: feature.place_name || "",
    };

    setQuery(feature.place_name || "");
    setSuggestions([]);
    setIsOpen(false);
    onSelect(geocoded);
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      {label && <Label className="mb-1.5">{label}</Label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9"
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-[200px] overflow-auto">
          {suggestions.map((feature) => (
            <li
              key={feature.id}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors"
              onClick={() => handleSelect(feature)}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <span>{feature.place_name}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
